/**
 * Skrzynka: praca przypisana konkretnej osobie.
 *
 * Osobna od alertów i tak ma zostać. Alert mówi „coś jest nie tak z produktem"
 * i widzą go wszyscy; wpis w skrzynce mówi „to jest teraz twoje" i ma dokładnie
 * jednego adresata. Wspólna lista odbierałaby wagę obu — alert utonąłby wśród
 * przypisań, a przypisanie byłoby kolejną awarią do odklikania.
 *
 * Kliknięcie wpisu robi dwie rzeczy naraz: odhacza go i otwiera zadanie.
 * Skrzynka, w której trzeba osobno odhaczyć i osobno przejść do pracy, po
 * tygodniu ma licznik, którego nikt nie zeruje.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Card, Chip, Empty, ErrorState, Loading } from "../components/ui";
import { api } from "../lib/api";
import { ago, dateTime, LABELS } from "../lib/format";
import type { Notification } from "../lib/types";

/** Odmiana przez liczbę — „1 nowy wpis", „3 nowe wpisy", „7 nowych wpisów". */
function wpisy(count: number): string {
  if (count === 1) return "nowy wpis";
  const last = count % 10;
  const teen = count % 100;
  return last >= 2 && last <= 4 && (teen < 12 || teen > 14) ? "nowe wpisy" : "nowych wpisów";
}

export function InboxPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const inbox = useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      api.get<{ notifications: Notification[]; unread: number }>("/notifications"),
    refetchInterval: 2 * 60_000,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["notifications"] });

  const read = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: refresh,
  });

  const readAll = useMutation({
    mutationFn: () => api.post("/notifications/read-all"),
    onSuccess: refresh,
  });

  const items = inbox.data?.notifications ?? [];
  const unread = inbox.data?.unread ?? 0;

  const open = (item: Notification) => {
    if (!item.read_at) read.mutate(item.id);
    if (item.entity_id) navigate(`/projekty?zadanie=${item.entity_id}`);
  };

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Ty</p>
          <h1>Skrzynka</h1>
          <p className="page-head__sub">
            Praca przypisana Tobie. Alerty dotyczą produktów i stoją osobno.
          </p>
        </div>
      </header>

      <Card
        title={unread > 0 ? `${unread} ${wpisy(unread)}` : "Wszystko przeczytane"}
        note="Kliknięcie otwiera zadanie i odhacza wpis."
        flush
        action={
          unread > 0 ? (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={readAll.isPending}
              onClick={() => readAll.mutate()}
            >
              {readAll.isPending ? "Odhaczam…" : "Oznacz wszystkie jako przeczytane"}
            </button>
          ) : undefined
        }
      >
        {inbox.isPending ? (
          <div style={{ padding: "var(--space-5)" }}>
            <Loading />
          </div>
        ) : inbox.isError ? (
          <div style={{ padding: "var(--space-5)" }}>
            <ErrorState error={inbox.error} />
          </div>
        ) : !items.length ? (
          <div style={{ padding: "var(--space-5)" }}>
            <Empty
              title="Pusto"
              hint="Wpis pojawia się tutaj, gdy ktoś przypisze Ci zadanie albo krok w zadaniu."
            />
          </div>
        ) : (
          <ul className="inbox">
            {items.map((item) => (
              <li key={item.id} className={item.read_at ? "inbox__row" : "inbox__row inbox__row--new"}>
                <span className="inbox__dot" aria-hidden="true" />
                <div className="inbox__body">
                  <p className="inbox__kind">
                    {LABELS.notification[item.type] ?? item.type}
                    {item.actor_label && <span className="dim"> · od {item.actor_label}</span>}
                  </p>
                  <button type="button" className="inbox__title" onClick={() => open(item)}>
                    {item.title}
                  </button>
                  {item.body && <p className="inbox__context">w zadaniu: {item.body}</p>}
                </div>
                <div className="inbox__side">
                  <span className="inbox__when mono" title={dateTime(item.created_at)}>
                    {ago(item.created_at)}
                  </span>
                  {item.email_status === "failed" && (
                    <Chip tone="warn" title="Powiadomienie jest tutaj, ale mail nie wyszedł">
                      {LABELS.emailStatus.failed}
                    </Chip>
                  )}
                  {!item.read_at && (
                    <button
                      type="button"
                      className="btn btn--quiet"
                      onClick={() => read.mutate(item.id)}
                    >
                      Odhacz
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
